import React, { useEffect, useRef, useState } from 'react';

import { useDeepCompareEffectForMaps } from '../../utils/googleMapHelpers';

export interface MapEventListener {
  eventName: string;
  handler: Function;
}

export interface MapProps extends google.maps.MapOptions {
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  onMapLoad?: (map: google.maps.Map) => void;
  eventListeners?: MapEventListener[];
}

export const GoogleMap: React.FC<MapProps> = ({ onClick, onIdle, onMapLoad, children, style, eventListeners, ...options }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (ref.current && !map) {
      const googleMap = new window.google.maps.Map(ref.current, {});
      onMapLoad(googleMap);
      setMap(googleMap);
    }
  }, [ref, map]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  useEffect(() => {
    if (map) {
      if (eventListeners) {
        eventListeners?.forEach((listner: MapEventListener) => {
          google.maps.event.clearListeners(map, listner.eventName);
          map.addListener(listner.eventName, listner.handler);
        });
      }

      ['click', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map } as any);
        }
      })}
    </>
  );
};
